/**
 * App Sessions Store
 *
 * Tracks recently opened apps for the multitasking/app switcher view.
 * Persisted to localStorage so sessions survive page reloads.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AppSession {
  /** App identifier */
  appId: string;
  /** Display name */
  label: string;
  /** App icon (emoji or URL) */
  icon: string;
  /** Route used to open the app */
  route: string;
  /** Timestamp when app was last opened */
  lastOpenedAt: number;
}

interface AppSessionsState {
  /** Recently opened apps (newest first, max 12) */
  sessions: AppSession[];
  /** Track an app being opened */
  pushSession: (session: Omit<AppSession, 'lastOpenedAt'>) => void;
  /** Remove a single app from recents */
  removeSession: (appId: string) => void;
  /** Clear all recent sessions */
  clearAll: () => void;
}

const MAX_SESSIONS = 12;

export const useAppSessionsStore = create<AppSessionsState>()(
  persist(
    (set) => ({
      sessions: [],

      pushSession: (session) =>
        set((state) => {
          const filtered = state.sessions.filter((s) => s.appId !== session.appId);
          const newSession: AppSession = {
            ...session,
            lastOpenedAt: Date.now(),
          };
          return {
            sessions: [newSession, ...filtered].slice(0, MAX_SESSIONS),
          };
        }),

      removeSession: (appId) =>
        set((state) => ({
          sessions: state.sessions.filter((s) => s.appId !== appId),
        })),

      clearAll: () => set({ sessions: [] }),
    }),
    {
      name: 'room-app-sessions',
      partialize: (state) => ({ sessions: state.sessions }),
    }
  )
);
